import type { AppTranslateFn } from "@/hooks/use-app-translation";
import type { TicketTypeDisplay } from "@/services/ticket/ticket.types";
import {
	type ConfigurationTypeTableAccessDisplay,
	type ConfigurationTypeTableAccessKeys,
	getConfigurationTypeTableAccessDisplay,
	getConfigurationTypeTableAccessTooltip,
} from "@/utils/configuration-type-table-access/configuration-type-table-access.utils";

export type TicketTypeScope = "platform" | "organization";

export function normalizeTicketTypeSource(row: TicketTypeDisplay): string {
	if (typeof row.defaultType === "boolean") {
		return row.defaultType ? "system" : "custom";
	}
	return (row.source ?? "").trim().toLowerCase();
}

export function isSystemTicketType(row: TicketTypeDisplay): boolean {
	if (typeof row.defaultType === "boolean") return row.defaultType;
	const source = (row.source ?? "").trim().toLowerCase();
	return source === "" || source === "system";
}

export function isCustomTicketType(row: TicketTypeDisplay): boolean {
	if (typeof row.defaultType === "boolean") return !row.defaultType;
	return (row.source ?? "").trim().toLowerCase() === "custom";
}

export function canShowActivityTab(
	scope: TicketTypeScope,
	_row: TicketTypeDisplay,
): boolean {
	if (scope === "platform" || scope === "organization") return true;
	return false;
}

export function canShowAccessTab(scope: TicketTypeScope): boolean {
	return scope === "organization";
}

export function getTicketTypeDisplayName(row: TicketTypeDisplay): string {
	return row.name?.trim() || "";
}

/** Org-wide disabled when `enabled === false`. */
export function isTicketTypeOrgAccessDisabled(
	row: Pick<TicketTypeDisplay, "enabled">,
): boolean {
	return row.enabled === false;
}

/**
 * Org-admin configuration table: all types remain visible when org-disabled
 * so Access tab changes are reflected in the table (e.g. "Not available").
 */
export function shouldShowOrgAdminTicketTypesTableRow(
	_row: Pick<TicketTypeDisplay, "defaultType" | "enabled" | "source">,
): boolean {
	return true;
}

export type TicketTypeTableAccessDisplay = ConfigurationTypeTableAccessDisplay;

const TICKET_TYPE_TABLE_ACCESS_KEYS = {
	available: "admin.ticketTypes.table.access.available",
	notAvailable: "admin.ticketTypes.table.access.notAvailable",
	noSubOrganizations: "org-admin.ticketTypes.table.access.noSubOrganizations",
	allSubOrganizations: "org-admin.ticketTypes.table.access.allSubOrganizations",
	partial: "admin.ticketTypes.table.access.partial",
	tooltipNone: "admin.ticketTypes.table.access.tooltipNone",
} satisfies ConfigurationTypeTableAccessKeys;

/** Org-admin Ticket Types table/export Access column label and badge color. */
export function getTicketTypeTableAccessDisplay(
	row: TicketTypeDisplay,
	totalSubOrganizations: number,
	t: AppTranslateFn,
): TicketTypeTableAccessDisplay {
	return getConfigurationTypeTableAccessDisplay({
		enabled: row.enabled,
		grantedCount: row.ticketTypeSubOrganizations?.length ?? 0,
		totalSubOrganizations,
		keys: TICKET_TYPE_TABLE_ACCESS_KEYS,
		t,
	});
}

/** Org-admin Ticket Types table Access column hover (sub-org names or "none"). */
export function getTicketTypeTableAccessTooltip(
	row: TicketTypeDisplay,
	t: AppTranslateFn,
): string {
	return getConfigurationTypeTableAccessTooltip({
		grantedSubOrganizations: row.ticketTypeSubOrganizations ?? [],
		keys: TICKET_TYPE_TABLE_ACCESS_KEYS,
		t,
	});
}
